/**
 * Centralized service configuration exports
 * This file provides a single entry point for all service configurations
 */

// Import service configurations for internal use
import { adminServices, adminServiceUtils } from './admin';
import { AUTHENTICATION_SERVICE_NAME, authenticationServices, type AuthenticationService } from './authentication';
import { createAuthorizationCustomService } from './authorization';
import { collateralServices, collateralServiceUtils, createCollateralCustomService } from './collateral';
import { configurationServices, configurationServiceUtils, createConfigurationCustomService } from './configuration';
import { dealcardServices, dealcardServiceUtils, createDealcardCustomService } from './dealcard';
import { getFederatedServicesForRemote, getLegacyCombinedFederationContext } from './federatedRemoteServices';
import { fundingServices, fundingServiceUtils, createFundingCustomService } from './funding';
import { investorServices, investorServiceUtils, createInvestorCustomService } from './investor';
import { reportingServices, reportingServiceUtils, createReportingCustomService } from './reporting';
import { dashboardServiceUtils, dashboardServices } from './dashboard';
import { createTitanCustomService, titanServiceUtils, titanServices } from './titan';

// Re-export service configurations
export { adminServices, adminServiceUtils };
export { AUTHENTICATION_SERVICE_NAME, authenticationServices, type AuthenticationService };
export { createAuthorizationCustomService };
export { collateralServices, collateralServiceUtils, createCollateralCustomService };
export { configurationServices, configurationServiceUtils, createConfigurationCustomService };
export { dashboardServices, dashboardServiceUtils };
export { dealcardServices, dealcardServiceUtils, createDealcardCustomService };
export { getFederatedServicesForRemote, getLegacyCombinedFederationContext };
export { fundingServices, fundingServiceUtils, createFundingCustomService };
export { investorServices, investorServiceUtils, createInvestorCustomService };
export { reportingServices, reportingServiceUtils, createReportingCustomService };
export { titanServices, titanServiceUtils, createTitanCustomService };
// Re-export the ServiceConfig type for convenience
export type { ServiceConfig } from '../ServiceRegistry';

/**
 * Get all service configurations for a specific module
 */
export const getModuleServices = (moduleName: string) => {
  switch (moduleName) {
    case 'dealcard':
      return dealcardServices;
    case 'funding':
      return fundingServices;
    case 'collateral':
      return collateralServices;
    case 'investor':
      return investorServices;
    case 'reporting':
      return reportingServices;
    case 'configuration':
      return configurationServices;
    case 'titan':
      return titanServices;
    case 'admin':
      return adminServices;
    case 'dashboard':
    case 'aga-dashboard':
      return dashboardServices;
    default:
      return [];
  }
};

/**
 * Get all service configurations for multiple modules
 */
export const getCombinedServices = (modules: string[]) => {
  return modules.flatMap(module => getModuleServices(module));
};

/**
 * Get all available module names
 */
export const getAvailableModules = () => [
  'dealcard',
  'funding',
  'collateral',
  'investor',
  'reporting',
  'configuration',
  'titan',
  'admin',
  'dashboard',
];

/**
 * Check if a module name is valid
 */
export const isValidModule = (moduleName: string) => {
  return getAvailableModules().includes(moduleName);
};
